import React, { useMemo, useState } from "react";
import { css } from "@emotion/react";
import { useLocation, useParams } from "react-router-dom";
import { Checkbox, PageTable, Title } from "@src/components";
import { ConvertPrice } from "@src/utils/units";
import { getColumns } from "./LectureRegisterColumns";
import { initLectureRegisterFilters, useLectureList, useLectureRegisters } from "./service";
import { LectureClassDto, LectureListDto, LectureRegisterFilterType } from "./service/types";

const LecturesApply = () => {
    const { id } = useParams();
    const location = useLocation();
    const lectureNo = Number(id);
    const columns = getColumns();

    const [params, setParams] = useState<LectureRegisterFilterType>(initLectureRegisterFilters);

    const stateLecture = (location.state ?? undefined) as LectureListDto | undefined;
    // 직접 URL 접근 시 강의 정보(클래스 목록)가 없으므로 목록 API로 조회
    const { data: lectureListData } = useLectureList({ page: 1, limit: 100 }, !stateLecture);

    const lecture: LectureListDto | undefined = useMemo(() => {
        if (stateLecture) {
            return stateLecture;
        }
        return lectureListData?.list.find((item) => item.no === lectureNo);
    }, [stateLecture, lectureListData, lectureNo]);

    const { data } = useLectureRegisters(lectureNo, params);

    const handleClassChange = (classNo: number, checked: boolean) => {
        setParams({
            ...params,
            page: 1,
            classNo: checked ? classNo : undefined,
        });
    };

    return (
        <div css={listStyle}>
            <Title title="강의 신청 관리" />
            <div css={filterBoxStyle}>
                <span className="subTitle">강의</span>
                <div css={checkboxListStyle}>
                    {(lecture?.classes ?? []).map((lectureClass: LectureClassDto) => (
                        <Checkbox
                            key={lectureClass.no}
                            label={lectureClass.title}
                            isChecked={params.classNo === lectureClass.no}
                            handleChange={(_key, checked) => {
                                handleClassChange(lectureClass.no, checked);
                            }}
                        />
                    ))}
                </div>
            </div>
            <div css={tableDivStyle}>
                <div css={countDivStyle}>
                    <span>총합 매출 금액: {ConvertPrice(data?.totalSalesAmount ?? 0)}</span>
                </div>
                <PageTable
                    columns={columns}
                    data={data?.list ?? []}
                    initialState={{
                        pageIndex: 0,
                        pageSize: params.limit,
                    }}
                    FixedHeight={500}
                    showTotalCount={true}
                    totalCount={data?.totalCnt ?? 0}
                    handlePageChange={(index: number) => {
                        const page = index + 1;
                        setParams({ ...params, page });
                    }}
                    forcePageNumber={params.page}
                ></PageTable>
            </div>
        </div>
    );
};

export default LecturesApply;

const listStyle = css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 40px;
`;

const filterBoxStyle = css`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 25px;
    padding: 24px;
    background-color: #f4f5f7;
    border-radius: 8px;
    font-size: 15px;
    color: #222222;

    & > .subTitle {
        width: 72px;
        flex-shrink: 0;
    }
`;

const checkboxListStyle = css`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
`;

const tableDivStyle = css`
    margin-top: 43px;
    width: 100%;
    height: 80vh;
`;

const countDivStyle = css`
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
`;
